import { ConnectQueryKeys } from '~/connect/api';
import {
  CrudOptions,
  OverridableQuery,
  UseQueryOptions,
  serialize,
  useCrud,
  useQuery,
  useMutation,
  UseMutationOptions,
} from '@wyz/utils';
import { useApi } from '~/common/context';
import {
  CustomerCriterion,
  IAnonymizedSupplierPlatform,
  ICriteriaUsage,
  ICriteriaUsageVariable,
  IWheelSupplierTemplate,
  IWheelSupplierTemplatePayload,
} from '~/connect/types';
import {
  IErrorApiResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import { useConfig } from '~/common/context';
import { CommonErrorCodesEnumType } from '~/common/types';
import { isNil } from 'lodash-es';

type Actions = 'create' | 'update' | 'getOneById' | 'remove';

type Options = Pick<
  CrudOptions<
    IWheelSupplierTemplate,
    IWheelSupplierTemplatePayload,
    IWheelSupplierTemplatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >,
  Actions
> & {
  list?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IWheelSupplierTemplate>>,
      IErrorApiResponse
    >
  >;
  getMatched?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IWheelSupplierTemplate | null>,
      IErrorApiResponse<unknown, CommonErrorCodesEnumType>
    >
  > & {
    args: {
      customerCriteria: Array<CustomerCriterion>;
      customerInternalCriteria: Array<CustomerCriterion>;
    };
  };
  checkCriteriaUsage?: UseMutationOptions<
    ISuccessApiResponse<ICriteriaUsage>,
    IErrorApiResponse,
    ICriteriaUsageVariable
  >;
  eligibleSuppliers?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IAnonymizedSupplierPlatform>>,
      IErrorApiResponse
    >
  >;
};

export const useManageWheelSupplierTemplates = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi, suppliersDomainBaseUrl } = useApi();
  const { tenantCode } = useConfig();

  const api = useCrud<
    Actions,
    IWheelSupplierTemplate,
    IWheelSupplierTemplatePayload,
    IWheelSupplierTemplatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >(
    {
      baseKey: ConnectQueryKeys.wheelSupplierTemplates.base(),
      caller: callCoreApi,
      endpoint: `/tenants/api/wtb/tenants/${tenantCode}/wheels/supplier-templates`,
    },
    options,
  );

  const listWheelSupplierTemplatesQuery = useQuery({
    ...options.list,
    queryKey: ConnectQueryKeys.wheelSupplierTemplates.base(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IWheelSupplierTemplate>>>(
        `/tenants/api/wtb/tenants/${tenantCode}/wheels/supplier-templates`,
      ),
    enabled: Boolean(options.list?.enabled),
  });

  const matchedWheelSupplierTemplatesQuery = useQuery({
    ...options.getMatched,
    queryKey: [
      ...ConnectQueryKeys.wheelSupplierTemplates.base(),
      'match',
      options.getMatched?.args,
    ],
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IWheelSupplierTemplate | null>>(
        `/tenants/api/wtb/tenants/${tenantCode}/wheels/supplier-templates/matched?${serialize(
          options.getMatched?.args,
        )}`,
      ),
    enabled:
      !isNil(options.getMatched?.args?.customerCriteria) &&
      Boolean(options.getMatched?.enabled),

    retry: false,
    gcTime: 0,
  });

  const eligibleSuppliersQuery = useQuery({
    ...options.eligibleSuppliers,
    queryKey: [
      ...ConnectQueryKeys.wheelSupplierTemplates.base(),
      'eligible-suppliers',
    ],
    queryFn: async () => {
      const response = await callCoreApi<
        ISuccessApiResponse<
          Array<{
            wyzOffer: boolean;
            supplier: IAnonymizedSupplierPlatform['supplierPlatform']['supplier'];
          }>
        >
      >(
        `${suppliersDomainBaseUrl}/tenants/${tenantCode}/supplier-platforms/wheel-eligible`,
      );

      return {
        ...response,
        data: response.data.map((sp) => ({
          supplierPlatform: sp,
          deliveryCountryIds: [],
        })),
      };
    },
    enabled: Boolean(options.eligibleSuppliers?.enabled),
  });

  const checkCriteriaUsage = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<ICriteriaUsage>>(
        `/tenants/api/wtb/tenants/${tenantCode}/wheels/supplier-templates/${
          variables.id
        }/check-usage?${serialize(variables.query)}`,
      ),
    ...options.checkCriteriaUsage,
  });

  return {
    ...api,
    list: listWheelSupplierTemplatesQuery,
    getMatched: matchedWheelSupplierTemplatesQuery,
    checkCriteriaUsage,
    eligibleSuppliers: eligibleSuppliersQuery,
  };
};
